import { unix } from 'moment';
import numeral from 'numeral';
import { parse, stringify } from 'query-string';
import { times, constant, isArray } from 'lodash';

/**
 * Get element offset
 *
 * @param {HTMLElement} el Element
 * @param {number} offset Additional offset
 * @return {number} Element offset
 */
const getElementY = (el, offset = 0) => {
  if (el && el.getBoundingClientRect) {
    return window.pageYOffset + (el.getBoundingClientRect().top - offset);
  }

  return 0;
};

export const keyCodes = {
  esc: 27,
  space: 32,
  tab: 9,
  up: 38,
  down: 40,
};

/**
 * Decode HTML that was escaped as text, including mixed markup like
 * `<p>&lt;p&gt;text&lt;/p&gt;</p>` from the offers API.
 * Plain HTML without `&lt;` is returned as-is (backward compatible).
 * @param {string} markup HTML markup
 * @return {string} HTML markup safe to inject
 */
export const decodeHtmlEntities = (markup = '') => {
  if (typeof markup !== 'string') {
    return '';
  }
  if (markup.indexOf('&lt;') === -1 || typeof document === 'undefined') {
    return markup;
  }

  const container = document.createElement('div');
  container.innerHTML = markup;

  // Only unwrap when the node text is a full HTML snippet, e.g. "<p>…</p>".
  // Skips normal copy like "ставка &lt; 5%".
  const looksLikeEscapedHtml = (value) => /^\s*<[a-z][^>]*>/i.test(value);

  if (!container.children.length && looksLikeEscapedHtml(container.textContent || '')) {
    container.innerHTML = container.textContent;
    return container.innerHTML;
  }

  Array.from(container.querySelectorAll('*')).forEach((el) => {
    if (el.children.length > 0) {
      return;
    }
    const text = el.textContent || '';
    if (!looksLikeEscapedHtml(text)) {
      return;
    }
    const wrap = document.createElement('div');
    wrap.innerHTML = text;
    el.replaceWith(...Array.from(wrap.childNodes));
  });

  return container.innerHTML;
};

/**
 * Create markup
 * @param {string} markup HTML markup
 * @return {Object} Object with a __html key
 * @see {@link https://reactjs.org/docs/dom-elements.html#dangerouslysetinnerhtml React Documentation}
 */
export const createMarkup = (markup) => ({ __html: decodeHtmlEntities(markup) });

/**
 * Utility for simple work with cookies
 */
export const Cookies = {
  /**
   * Get cookie's value
   * @param {string} name Cookie name
   * @return {string} Cookie value
   */
  get(name) {
    const matches = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'),
    );

    return matches ? decodeURIComponent(matches[1]) : undefined;
  },

  /**
   * Set cookie's value
   * @param {string} name Cookie name
   * @param {string|number} value Cookie value
   * @param {Object} options Cookie options
   */
  set(name, value, options = {}) {
    let expires = options.expires;

    if (typeof expires === 'number' && expires) {
      const d = new Date();
      d.setTime(d.getTime() + expires * 1000);
      expires = options.expires = d;
    }

    if (expires && expires.toUTCString) {
      options.expires = expires.toUTCString();
    }

    value = encodeURIComponent(value);

    let updatedCookie = `${name} = ${value}`;

    for (let propName in options) {
      updatedCookie += `; ${propName}`;
      let propValue = options[propName];

      if (propValue !== true) {
        updatedCookie += '=' + propValue;
      }
    }

    document.cookie = updatedCookie;
  },

  /**
   * Remove cookie
   * @param {string} name Cookie name
   */
  remove(name) {
    this.set(name, '', { expires: -1 });
  },
};

export const LocalStorage = {
  get(name) {
    return JSON.parse(window.localStorage.getItem(name));
  },

  set(name, value) {
    window.localStorage.setItem(name, JSON.stringify(value));
  },

  remove(name) {
    window.localStorage.removeItem(name);
  },
};

/**
 * Is property a null or undefined or empty array
 * @param {*} prop Property
 * @return {boolean} Result
 */
export const isNil = (prop) =>
  prop === null || prop === undefined || (Array.isArray(prop) && prop.length === 0);

/**
 * Is property a string or not
 * @param {*} prop Property
 * @return {boolean} Result of typeof comparsion
 */
export const isString = (prop) => typeof prop === 'string';

/**
 * Get only unique values
 * @param {Array} array Array of values
 * @return {Array} Array of unique values
 */
export const getUnique = (array = []) => array.filter((v, i, a) => a.indexOf(v) === i);

/**
 * Get API base URL
 * @param {boolean} isProduction Current environment
 * @return {string} API base URL
 */
export const getBaseURL = (isProduction) => {
  let domain = window.location.hostname.replace('my', 'api');
  if (domain.includes('.devstage.ttplace.online')) {
    return 'https://api.devstage.ttplace.online';
  }

  if (domain.includes('.local')) {
    return 'http://api.incus.local';
  }

  if (domain === 'localhost') domain = 'offerrum.test';

  let url = `https://api.s1.stage.offerrum.com`;

  if (isProduction) {
    url = `//${domain}`;
  } else {
    const local = process.env.LOCAL_API_URL;
    url = local ? local : url;
  }

  return url;
};

/**
 * Return chat's visibility status
 * @return {boolean} Status
 */
export const isChatVisible = () => false;

/**
 * Compare unix timestamps
 * @param {number} a First timestamp
 * @param {number} b Second timestamp
 * @param {string} measurement Measurement (day, month, etc)
 * @return {boolean} Result of a is equal b
 */
export const compareTs = (a = 0, b = 0, measurement = 'day') =>
  unix(a).isSame(unix(b), measurement);

/**
 * Return chunked array
 * @param {Array} arr Array
 * @param {number} size Chunk size
 * @return {Array} Chunked array of arrays
 */
export const chunk = (arr = [], size) => {
  var R = [];
  for (var i = 0; i < arr.length; i += size) R.push(arr.slice(i, i + size));
  return R;
};

/**
 * Animated scroll to node
 * @param {HTMLElement} el Element to scroll
 * @param {number} duration Scroll duration in ms
 * @param {number} [offset] Offset from top
 */
export const animatedScrollTo = (el, duration, offset = 0) => {
  const startingY = window.pageYOffset;
  const elementY = getElementY(el, offset);

  // If element is close to page's bottom then window will scroll only to some position above the element.
  const targetY =
    document.body.scrollHeight - elementY < window.innerHeight
      ? document.body.scrollHeight - window.innerHeight
      : elementY;

  const diff = targetY - startingY;

  // Easing function: easeInOutCubic
  // From: https://gist.github.com/gre/1650294
  const easing = (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1);

  let start;

  if (!diff) return;

  // Bootstrap our animation - it will get called right before next frame shall be rendered.
  window.requestAnimationFrame(function step(timestamp) {
    if (!start) start = timestamp;

    // Elapsed miliseconds since start of scrolling.
    const time = timestamp - start;

    // Get percent of completion in range [0, 1].
    let percent = Math.min(time / duration, 1);

    // Apply the easing.
    // It can cause bad-looking slow frames in browser performance tool, so be careful.
    percent = easing(percent);

    window.scrollTo(0, startingY + diff * percent);

    // Proceed with animation as long as we wanted it to.
    if (time < duration) {
      window.requestAnimationFrame(step);
    }
  });
};

/**
 * Корректировка округления десятичных дробей.
 * @param {String}  type  Тип корректировки.
 * @param {Number}  value Число.
 * @param {Integer} exp   Показатель степени (десятичный логарифм основания корректировки).
 * @return {Number} Скорректированное значение.
 *
 * @see {@link https://developer.mozilla.org/ru/docs/Web/JavaScript/Reference/Global_Objects/Math/round MDN}
 */
export const decimalAdjust = (type, value, exp) => {
  // Если степень не определена, либо равна нулю...
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math[type](value);
  }

  value = +value;
  exp = +exp;

  // Если значение не является числом, либо степень не является целым числом...
  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN;
  }
  // Сдвиг разрядов
  value = value.toString().split('e');
  value = Math[type](+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));

  // Обратный сдвиг
  value = value.toString().split('e');

  return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
};

/**
 * Dispatch Google Analytics event
 * @param {Object} event
 * @return {boolean} Is event dispatched
 */
export const triggerGAEvent = ({ action, category, label, value, dataLayerName = 'dataLayer' }) => {
  if (window[dataLayerName]) {
    window[dataLayerName].push({
      event: 'event-to-ga',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    });

    return true;
  }

  return false;
};

/**
 * Format number to currency string
 * @param {number} value
 * @param {string} [format='0,0.00']
 * @return {string} Formatted value
 */

export const formatToCurrency = (value = 0, format = '0,0.00') => {
  const splitStr = (value || 0).toString().split('.');
  const wholePart = splitStr[0];
  const fractionPart = splitStr.length > 1 ? splitStr[1].slice(0, 2) : 0;
  let number = [wholePart, fractionPart].join('.');

  return numeral(number).format(format);
};

const restArguments = (func, startIndex) => {
  startIndex = startIndex == null ? func.length - 1 : +startIndex;

  return function () {
    const length = Math.max(arguments.length - startIndex, 0);
    const rest = Array(length);
    let index = 0;

    for (; index < length; index++) {
      rest[index] = arguments[index + startIndex];
    }

    switch (startIndex) {
      case 0:
        return func.call(this, rest);
      case 1:
        return func.call(this, arguments[0], rest);
      case 2:
        return func.call(this, arguments[0], arguments[1], rest);
    }

    const args = Array(startIndex + 1);

    for (index = 0; index < startIndex; index++) {
      args[index] = arguments[index];
    }

    args[startIndex] = rest;

    return func.apply(this, args);
  };
};

export const debounce = (func, wait, immediate) => {
  let timeout;
  let result;

  const later = (context, args) => {
    timeout = null;
    if (args) result = func.apply(context, args);
  };

  const debounced = restArguments(function (args) {
    if (timeout) clearTimeout(timeout);

    if (immediate) {
      const callNow = !timeout;
      timeout = setTimeout(later, wait);
      if (callNow) result = func.apply(this, args);
    } else {
      timeout = _.delay(later, wait, this, args);
    }

    return result;
  });

  debounced.cancel = () => {
    clearTimeout(timeout);
    timeout = null;
  };

  return debounced;
};

/**
 * Remove double slashes in URL
 *
 * @param {string} url
 * @return {string} Fixed URL
 */
export const removeDoubleSlashes = (url) => url.replace(/([^:]\/)\/+/g, '$1');

/**
 * Pluralize
 *
 * @param {number} number quantity
 * @param {string} one
 * @param {string} two
 * @param {string} five
 * @return {string} Pluralized string
 */
export const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;

  if (n >= 5 && n <= 20) {
    return five;
  }

  n %= 10;

  if (n === 1) {
    return one;
  }

  if (n >= 2 && n <= 4) {
    return two;
  }

  return five;
};

/**
 * Convert currency code to sign
 *
 * @param {string} currency
 * @return {string} Currency sign.
 */
export const currencyToSign = (currency) => {
  if (!currency) return;

  switch (currency.toLowerCase()) {
    case 'usd':
      return '$';
    case 'eur':
      return '€';
    case 'rub':
      return '₽';
    case 'gbp':
      return '£';
    case 'percent':
      return '%';
    default:
      return currency;
  }
};

export const getZoneOffsetByName = (zones, name) => {
  const zone = zones.find((z) => z.name === name);
  if (zone) {
    return zone.value.replace('UTC', '');
  }
  return '';
};

export const isElemInViewport = (el) => {
  const { pageYOffset, pageXOffset, innerHeight, innerWidth } = window;
  const width = el.offsetWidth;
  const height = el.offsetHeight;
  let top = el.offsetTop;
  let left = el.offsetLeft;

  while (el.offsetParent) {
    el = el.offsetParent;
    top += el.offsetTop;
    left += el.offsetLeft;
  }

  return (
    top >= pageYOffset &&
    left >= pageXOffset &&
    top + height <= pageYOffset + innerHeight &&
    left + width <= pageXOffset + innerWidth
  );
};

export const getStoreUrl = () => {
  const origin = window.location.origin;
  const regEx = /my|front/g;
  const isMatch = origin.match(regEx);
  const prefix = isMatch ? 'store' : 'store.';
  if (isMatch) {
    return origin.replace(regEx, prefix);
  }
  return origin.replace('//', `//${prefix}`);
};

export const getContestUrl = () => {
  const origin = window.location.origin;
  const regEx = /my|front/g;
  const isMatch = origin.match(regEx);
  const prefix = isMatch ? 'win' : 'win.';
  if (isMatch) {
    return origin.replace(regEx, prefix);
  }
  return origin.replace('//', `//${prefix}`);
};

export const _toFixed = (value, n) => {
  const v = value.toString().split('.');
  if (n <= 0) return v[0];
  let f = v[1] || '';
  if (f.length > n) return `${v[0]}.${f.substr(0, n)}`;
  while (f.length < n) f += '0';
  return `${v[0]}.${f}`;
};

export const getToFixedParams = (config, statId) => {
  const params = {
    numFormat: 2,
    strFormat: '0,0.00',
  };
  const currentTab = config.find(({ id }) => id === statId);
  if (currentTab && currentTab.precision >= 0) {
    params.numFormat = currentTab.precision;
    params.strFormat = `0,0.${times(currentTab.precision, constant(0)).join('')}`;
  }
  return params;
};

export const isWeekend = (timestamp = 0, timeZone) => {
  const day = unix(timestamp).utcOffset(timeZone)._d.getDay();

  if (day === 0) {
    return '(вс)';
  } else if (day === 6) {
    return '(сб)';
  } else {
    false;
  }
};

export const getStringOrSingleArrayValue = (value) => (isArray(value) ? value[0] : value);

export const descSort = (list, param) => {
  if (list && list[0] && list[0][param]) {
    return list.sort((a, b) => b[param] - a[param]);
  }
  return list;
};

export const clearEmptyParams = (params) => {
  const notEmptyParams = {};
  Object.keys(params).map((key) => {
    if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
      notEmptyParams[key] = params[key];
    }
  });
  return notEmptyParams;
};

export const updateLocation = (params, clearParams) => {
  const parsedParams = parse(window.location.search);
  const queryParams = clearEmptyParams({ ...parsedParams, ...params });
  if (clearParams) {
    history.replaceState({}, null, window.location.pathname);
  } else {
    if (Object.keys(queryParams).length) {
      history.replaceState({}, null, `${window.location.pathname}?${stringify(queryParams)}`);
    }
  }
};

export const randomInteger = (min, max) => {
  const rand = min - 0.5 + Math.random() * (max - min + 1);
  return Math.round(rand);
};

export const randomString = (length) => {
  let result = '';
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const strignShuffle = (string = '') => {
  let a = string.split('');
  const n = a.length;

  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }
  return a.join('');
};

export const getErrorMessage = (error) => {
  if (error instanceof Error) {
    return error.response.data.error.message.message;
  }
};

export const prettifyAmount = (amount) => {
  let remain, main;
  if (amount) {
    if (amount % 10 !== 0) {
      main = String(amount).split('.')[0];
      remain = String(amount).split('.')[1];
    } else {
      main = String(amount);
    }

    const mainAsArray = String(main).split('');
    if (main > 999) {
      mainAsArray.splice(-3, 0, ' ');
    }
    if (main > 999999) {
      mainAsArray.splice(-7, 0, ' ');
    }
    const finalMain = mainAsArray.join('');
    return remain ? [finalMain, remain].join('.') : finalMain;
  } else {
    return amount;
  }
};

/**
 * Возвращает имя (title) числительное
 *
 * @param {Nubber} number
 * @param {Array} titles
 */
export const declOfNum = (number, titles) => {
  const cases = [2, 0, 1, 1, 1, 2];
  return titles[
    number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]
  ];
};

/**
 * Dynamically sets a deeply nested value in an object.
 * Optionally "bores" a path to it if its undefined.
 * @param {Object} obj  - The object which contains the value you want to change/set.
 * @param {Array} path  - The array representation of path to the value you want to change/set.
 * @param {*} value - The value you want to set it to.
 * @param {boolean} setrecursively - If true, will set value of non-existing path as well.
 */
export function setDeep(obj, path, value, setrecursively = false) {
  path.reduce((a, b, level) => {
    if (setrecursively && typeof a[b] === 'undefined' && level !== path.length - 1) {
      a[b] = {};
      return a[b];
    }

    if (level === path.length - 1) {
      a[b] = value;
      return value;
    }
    return a[b];
  }, obj);
}

/**
 * Finds nested value in object
 * @param {Object} obj - The object where to look for property.
 * @param {Array} path - The array representation of path to the value you want to get.
 * @returns {*} - Found value or null.
 */
export function findDeep(obj, path) {
  return path.reduce((acc, val, i) => {
    if (typeof acc === 'object' && acc !== null) {
      return i < path.length && acc[val] !== undefined ? acc[val] : null;
    } else {
      return acc;
    }
  }, obj);
}
